import { useContext, useRef } from "react";
import style from "../../../PennyDrop/PennyDrop.module.css";

// * Components
import TextInputField from "../../../../DataInputFields/TextInputField/TextInputField";
import ContinueButton from "../../../../Buttons/SlideContinueButton/ContinueButton";

// * Constants
import * as Errors from "../../../../../utils/errors";

import ErrorContext from "../../../../../context/ErrorProvider";
import { isBlank } from "../../../../../helpers";

export default function HomeLoanSanctionLetterDetails({
  updateJourneyData,
  switchToNextSlide,
  loading,
}) {
  const propertyPriceRef = useRef();
  const sanctionAmountRef = useRef();
  const downPaymentPayableRef = useRef();
  const downPaymentPaidToBuilderRef = useRef();
  const requiredLoanAmountRef = useRef();
  const emiAmountOnHomeLoanRef = useRef();
  const homeLoanInstitutionRef = useRef();

  const { error, setError, resetError } = useContext(ErrorContext);

  const validateBlankField = (value, errorType) => {
    if (isBlank(value)) {
      setError({
        error: true,
        type: errorType,
        message: Errors.REQUIRED_FIELD,
      });
      return false;
    } else {
      if (error.error && error.type === errorType) resetError();
      return true;
    }
  };

  const getFieldsValues = () => {
    const propertyPrice = parseInt(propertyPriceRef.current.value);
    const sanctionAmount = sanctionAmountRef.current.value;
    const downPaymentPayable = parseInt(downPaymentPayableRef.current.value);
    const downPaymentPaidToBuilder = parseInt(
      downPaymentPaidToBuilderRef.current.value
    );
    const requiredLoanAmount = parseInt(requiredLoanAmountRef.current.value);
    const emiAmountOnHomeLoan = parseInt(emiAmountOnHomeLoanRef.current.value);
    const homeLoanInstitution = homeLoanInstitutionRef.current.value;

    const fields = {
      propertyPrice:
        propertyPrice && !isNaN(propertyPrice) ? parseInt(propertyPrice) : null,
      sanctionAmount:
        sanctionAmount && !isNaN(sanctionAmount)
          ? parseInt(sanctionAmount)
          : null,
      downPaymentPayable:
        downPaymentPayable && !isNaN(downPaymentPayable)
          ? parseInt(downPaymentPayable)
          : null,
      downPaymentPaidToBuilder:
        downPaymentPaidToBuilder && !isNaN(downPaymentPaidToBuilder)
          ? parseInt(downPaymentPaidToBuilder)
          : null,
      requiredLoanAmount:
        requiredLoanAmount && !isNaN(requiredLoanAmount)
          ? parseInt(requiredLoanAmount)
          : null,
      emiAmountOnHomeLoan:
        emiAmountOnHomeLoan && !isNaN(emiAmountOnHomeLoan)
          ? parseInt(emiAmountOnHomeLoan)
          : null,
      homeLoanInstitution,
    };

    return fields;
  };
  const validateFields = (value, name) => {
    let isValid;
    switch (name) {
      case "propertyPrice":
        isValid = validateBlankField(value, Errors.PROPERTY_PRICE);
        break;
      case "downPaymentPayable":
        isValid = validateBlankField(value, Errors.DOWN_PAYMENT_PAYABLE);
        break;
      case "downPaymentPaidToBuilder":
        isValid = validateBlankField(
          value,
          Errors.DOWN_PAYMENT_PAID_TO_BUILDER
        );
        break;
      case "requiredLoanAmount":
        isValid = validateBlankField(value, Errors.REQUIRED_LOAN_AMOUNT);
        break;
      case "emiAmountOnHomeLoan":
        isValid = validateBlankField(value, Errors.EMI_AMOUNT_ON_HOME_LOAN);
        break;
      case "homeLoanInstitution":
        isValid = validateBlankField(value, Errors.HOME_LOAN_INSTITUTION);
        break;
      default:
        isValid = true;
    }

    return isValid;
  };

  const getCalcuatedAttributes = (sanctionLetterDetails) => {
    const {
      propertyPrice,
      downPaymentPayable,
      downPaymentPaidToBuilder,
      requiredLoanAmount,
    } = sanctionLetterDetails;

    const down_payment_percentage =
      (downPaymentPaidToBuilder / downPaymentPayable) * 100;

    // * Calculate Loan Amount Eligibility
    const lowerOfDownPaymentPaidByCustomerOrDesiredLoanAmount = Math.min(
      downPaymentPaidToBuilder,
      requiredLoanAmount
    );
    const fiftyPercentOfDownPaymentPayable = downPaymentPayable / 2;
    const tenPercentOfPropertyPrice = propertyPrice / 10;

    const eligible_loan_amount = Math.min(
      lowerOfDownPaymentPaidByCustomerOrDesiredLoanAmount,
      fiftyPercentOfDownPaymentPayable,
      tenPercentOfPropertyPrice
    );

    return { down_payment_percentage, eligible_loan_amount };
  };
  const handleSubmit = async () => {
    const sanctionLetterDetails = getFieldsValues();
    const fields = Object.keys(sanctionLetterDetails);

    let isValid;
    for (let i = 0; i < fields.length; i++) {
      isValid = validateFields(sanctionLetterDetails[fields[i]], fields[i]);
      if (!isValid) break;
    }

    if (isValid) {
      const slidePayload = {
        slideName: "Home Loan Sanction Letter Details",
        slideIndex: 49,
        slideIcon: "HomeLoanSanctionLetterDetails",
      };

      const {
        propertyPrice,
        sanctionAmount,
        downPaymentPayable,
        downPaymentPaidToBuilder,
        requiredLoanAmount,
        emiAmountOnHomeLoan,
        homeLoanInstitution,
      } = sanctionLetterDetails;

      let data = {
        property_price: propertyPrice,
        home_loan_sanction_amount: sanctionAmount,
        down_payment_payable: downPaymentPayable,
        down_payment_paid_to_builder: downPaymentPaidToBuilder,
        required_loan_amount: requiredLoanAmount,
        emi_amount_on_home_loan: emiAmountOnHomeLoan,
        home_loan_institution: homeLoanInstitution,
      };

      const { down_payment_percentage, eligible_loan_amount } =
        getCalcuatedAttributes(sanctionLetterDetails);

      data.down_payment_percentage = down_payment_percentage;
      data.eligible_loan_amount = eligible_loan_amount;

      await updateJourneyData(data, slidePayload);

      switchToNextSlide();
    }
  };

  return (
    <div className={style.slide}>
      <div className={style.slideHeader}>
        <h1>Sanction Letter Details</h1>
        <p>Please enter your Home Loan sanction letter details.</p>
      </div>

      <div className={style.inputFieldBox}>
        <TextInputField
          type="number"
          label="Property Price"
          placeholder="Property Price"
          name="propertyPrice"
          id="propertyPrice"
          reference={propertyPriceRef}
          handler={validateFields}
          error={error}
          errorType={Errors.PROPERTY_PRICE}
          required={true}
        />
        <TextInputField
          type="number"
          label="Home Loan Sanction Amount"
          placeholder="Home Loan Sanction Amount"
          name="sanctionAmount"
          id="sanctionAmount"
          reference={sanctionAmountRef}
          handler={validateFields}
          error={error}
          errorType={Errors.HOME_LOAN_SANCTION_AMOUNT}
        />
        <TextInputField
          type="number"
          label="Down Payment Payable"
          placeholder="Down Payment Payable"
          name="downPaymentPayable"
          id="downPaymentPayable"
          reference={downPaymentPayableRef}
          handler={validateFields}
          error={error}
          errorType={Errors.DOWN_PAYMENT_PAYABLE}
          required={true}
        />
        <TextInputField
          type="number"
          label="Down Payment Paid to Builder"
          placeholder="Down Payment Paid to Builder"
          name="downPaymentPaidToBuilder"
          id="downPaymentPaidToBuilder"
          reference={downPaymentPaidToBuilderRef}
          handler={validateFields}
          error={error}
          errorType={Errors.DOWN_PAYMENT_PAID_TO_BUILDER}
          required={true}
        />
        <TextInputField
          type="number"
          label="Required Loan Amount"
          placeholder="Required Loan Amount"
          name="requiredLoanAmount"
          id="requiredLoanAmount"
          reference={requiredLoanAmountRef}
          handler={validateFields}
          error={error}
          errorType={Errors.REQUIRED_LOAN_AMOUNT}
          required={true}
        />
        <TextInputField
          type="number"
          label="EMI Amount on Home Loan"
          placeholder="EMI Amount on Home Loan"
          name="emiAmountOnHomeLoan"
          id="emiAmountOnHomeLoan"
          reference={emiAmountOnHomeLoanRef}
          handler={validateFields}
          error={error}
          errorType={Errors.EMI_AMOUNT_ON_HOME_LOAN}
          required={true}
        />
        <TextInputField
          type="text"
          label="Home Loan Institution"
          placeholder="Home Loan Institution"
          name="homeLoanInstitution"
          id="homeLoanInstitution"
          reference={homeLoanInstitutionRef}
          handler={validateFields}
          error={error}
          errorType={Errors.HOME_LOAN_INSTITUTION}
          required={true}
        />
      </div>

      <div>
        <ContinueButton
          loading={loading}
          label="Continue"
          disabled={error.error}
          handler={handleSubmit}
        />
      </div>
    </div>
  );
}
